import { ref, type ComputedRef, type Ref } from 'vue';

export function useColumnResize(params: {
  columnConfigs: Ref<Map<string, { resizeable: boolean; [k: string]: unknown }>>;
  columnWidths: Ref<Map<string, number>>;
  minColWidth: ComputedRef<number>;
  /** Optional per-column floor (e.g. full header label width); wins over minColWidth. */
  minColWidthFor?: (columnId: string) => number;
  maxColWidth: ComputedRef<number>;
  /** Responsive columns may already be wider than the fixed-column limit. */
  maxColWidthFor?: (columnId: string) => number;
  /** Freeze responsive neighbours at their rendered widths before the first move. */
  onBeforeResize?: (columnId: string) => void;
  /**
   * Live width during the drag, applied OUTSIDE Vue reactivity (a CSS variable
   * on the table wrapper): writing every mousemove into the reactive width map
   * re-rendered the whole table — with heavy edit cells the drag visibly hung.
   * `null` clears the live value after the final width is committed.
   */
  liveResize: (columnId: string, px: number | null) => void;
  onResized: (columnId: string, newWidth: number) => void;
}) {
  const isResizeActive = ref<boolean>(false);

  function onResizeStart(startEvent: MouseEvent, columnId: string) {
    if (!params.columnConfigs.value.get(columnId)!.resizeable) {
      return;
    }
    const startWidth = (
      startEvent.currentTarget as HTMLElement
    ).parentElement!.getBoundingClientRect().width;
    const minWidth = Math.max(params.minColWidth.value, params.minColWidthFor?.(columnId) ?? 0);
    const maxWidth = params.maxColWidthFor?.(columnId) ?? params.maxColWidth.value;
    let prepared = false;
    let lastWidth = startWidth;
    const onMousemove = (event: MouseEvent) => {
      const delta = event.clientX - startEvent.clientX;
      lastWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
      if (!prepared) {
        if (lastWidth === startWidth) return;
        params.onBeforeResize?.(columnId);
        // A press alone must not turn automatic widths into fixed widths.
        params.columnWidths.value.set(columnId, startWidth);
        prepared = true;
      }
      params.liveResize(columnId, lastWidth);
    };
    document.addEventListener('mousemove', onMousemove);
    document.addEventListener(
      'mouseup',
      () => {
        document.removeEventListener('mousemove', onMousemove);
        isResizeActive.value = false;
        document.body.style.userSelect = '';
        // Commit once, then drop the live override.
        if (prepared) {
          params.columnWidths.value.set(columnId, lastWidth);
          params.liveResize(columnId, null);
        }
        if (startWidth !== lastWidth) {
          params.onResized(columnId, lastWidth);
        }
      },
      { once: true }
    );
    isResizeActive.value = true;
    document.body.style.userSelect = 'none';
  }

  return { isResizeActive, onResizeStart };
}
